import { env } from '../../config/env';
import { Topic, TopicSearchParams } from '../../types/topic';
import { ApiResponse, API_STATUS_CODES, PaginatedResponse } from '../../types/api';
import { get } from '../../utils/api';

export class TopicsService {
  private static instance: TopicsService;
  private baseUrl: string;

  private constructor() {
    this.baseUrl = `${env.apiBaseUrl}`;
  }

  static getInstance(): TopicsService {
    if (!TopicsService.instance) {
      TopicsService.instance = new TopicsService();
    }
    return TopicsService.instance;
  }

  private validateApiResponse<T>(response: ApiResponse<T>): PaginatedResponse<T> {
    if (response.statusCode !== API_STATUS_CODES.SUCCESS || !response.data) {
      throw new Error(response.message || 'API request failed');
    }
    return response.data as PaginatedResponse<T>;
  }

  private createQueryString(params: TopicSearchParams): string {
    const searchParams = new URLSearchParams();
    
    // Required params
    searchParams.append('limit', params.limit.toString());
    searchParams.append('page', params.page.toString());
    
    // Optional params
    if (params.term) searchParams.append('term', params.term);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortType) searchParams.append('sortType', params.sortType);
    
    return searchParams.toString();
  }

  async searchTopics(params: TopicSearchParams): Promise<PaginatedResponse<Topic>> {
    try {
      const queryParams: Record<string, string | number | boolean | string[] | undefined> = {
        page: params.page,
        limit: params.limit,
        term: params.term,
        sortBy: params.sortBy,
        sortType: params.sortType,
        parentTopic: params.parentTopic,
        language: params.language,
        status: params.status,
        organization: params.organization,
        includeChildren: params.includeChildren,
        recursive: params.recursive
      };
      
      const response = await get<ApiResponse<Topic>>('/topics', queryParams, {
        validateApiResponse: false,
        baseUrl: this.baseUrl
      });
      
      return this.validateApiResponse(response);
    } catch (error) {
      throw error;
    }
  }

  async getAllTopics(batchSize = 100): Promise<Topic[]> {
    let page = 1;
    let allTopics: Topic[] = [];
    let hasMore = true;

    while (hasMore) {
      const response = await this.searchTopics({
        page,
        limit: batchSize,
        sortBy: 'count',
        sortType: 'desc'
      });

      allTopics = [...allTopics, ...response.results];
      hasMore = allTopics.length < response.total;
      page++;
    }

    return allTopics;
  }

  async getTopicById(id: string): Promise<Topic> {
    try {
      const response = await get<ApiResponse<Topic>>(`/topics/${id}`, undefined, {
        validateApiResponse: false,
        baseUrl: this.baseUrl
      });
      
      if (response.statusCode !== API_STATUS_CODES.SUCCESS || !response.data) {
        throw new Error(response.message || 'Failed to get topic');
      }
      
      return response.data as Topic;
    } catch (error) {
      throw error;
    }
  }

  async getTopicsByParent(parentId: string, page = 1, limit = 20): Promise<PaginatedResponse<Topic>> {
    try {
      const params: Record<string, string | number | boolean | string[] | undefined> = {
        page,
        limit,
        sortBy: 'count',
        sortType: 'desc'
      };
      
      const response = await get<ApiResponse<Topic>>(`/topics/${parentId}/children`, params, {
        validateApiResponse: false,
        baseUrl: this.baseUrl
      });
      
      return this.validateApiResponse(response);
    } catch (error) {
      throw error;
    }
  }
}